import { PageHeader } from '../../components/office';
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import SubRecipesPanel from '../../components/menu/SubRecipesPanel';
import { useSite } from '../../contexts/SiteContext';
import { recipesApi, menuApi, ingredientsApi, suppliersApi, menuSectionsApi, subRecipesApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { SearchField } from '@verlo/shared/components/ui/search-field';
import { ALLERGENS, allergenLabel } from '@verlo/shared/lib/allergens';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Stat, Stats } from '../../components/office';
import { compareWithDish, fromRecipe } from '../../lib/recipeAllergens';

// Recipes (Back office sweep, Oct 2026). Every dish opens in one modal, whether it has a
// recipe or not; there's one Create recipe button, Save sits at the bottom, and the dish's
// allergens are worked out from its ingredients (D12). Where they differ from the dish's own
// list it says so in red, and an ingredient nobody has recorded allergens for asks for them.

const money = (value) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
const words = (keys) => keys.map(allergenLabel).join(', ');

const RecipesPage = () => {
  // Menu items are site-scoped and follow the header's picker; ingredients, recipes and
  // sub-recipes are org-scoped (`ownership.OWNER_TABLE`) and deliberately do not — one
  // costing book for the business.
  const { site } = useSite();
  const [menuItems, setMenuItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [subRecipes, setSubRecipes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sectionOrder, setSectionOrder] = useState({});   // the till's section order, from Structure
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // The recipe being made or changed
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState([]);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [newIngredientOpen, setNewIngredientOpen] = useState(false);
  const [newIngredient, setNewIngredient] = useState({ name: '', cost_per_unit: '', unit: 'kg', supplier_id: '' });
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [askingAllergens, setAskingAllergens] = useState(null);   // the line whose allergens are being recorded

  useEffect(() => { fetchData(); }, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [menuRes, recipesRes, ingredientsRes, suppliersRes, sectionsRes, subsRes] = await Promise.all([
        menuApi.getAll(undefined, site),
        recipesApi.getAll(),
        ingredientsApi.getAll(),
        suppliersApi.getAll(),
        menuSectionsApi.list(site).catch(() => ({ data: [] })),
        subRecipesApi.getAll().catch(() => ({ data: [] })),
      ]);
      setMenuItems(menuRes.data);
      setSectionOrder(Object.fromEntries((sectionsRes.data || []).map((sec, i) => [sec.name, i])));
      setRecipes(recipesRes.data);
      setIngredients(ingredientsRes.data);
      setSubRecipes(subsRes.data || []);
      setSuppliers(suppliersRes.data);
    } catch {
      toast.error("Couldn't load the recipes");
    } finally {
      setLoading(false);
    }
  };

  // What a recipe line can be made of, priced the same way whichever it is. A sub-recipe is
  // bought by the batch and used by the portion, so its per-unit price is
  // `batch_cost / yield_qty` — the same arithmetic the server costs it with. One without a
  // yield cannot be portioned at all, so it is not offered (the save would refuse it).
  const costables = useMemo(() => [
    ...ingredients.map((i) => ({ id: i.id, name: i.name, unit: i.unit, cost_per_unit: i.cost_per_unit, source: 'ingredient', allergens: i.allergens ?? null })),
    ...subRecipes.filter((r) => r.yield_qty > 0).map((r) => ({
      id: r.id, name: r.name, unit: r.yield_unit || 'unit', cost_per_unit: (r.batch_cost || 0) / r.yield_qty, source: 'sub_recipe', allergens: r.allergens ?? null,
    })),
  ], [ingredients, subRecipes]);
  const allergensOf = (line) => costables.find((c) => c.id === line.ingredient_id)?.allergens ?? null;

  // A stored recipe line carries `cost` — the line total — not `cost_per_unit`. Prefer the
  // live price (a recipe reopened after a supplier increase should edit at today's price),
  // and fall back to what the line was costed at.
  const editableLine = (ing) => {
    const live = costables.find((c) => c.id === ing.ingredient_id);
    return {
      ingredient_id: ing.ingredient_id, name: ing.name, quantity: ing.quantity, unit: ing.unit,
      source: ing.source || live?.source || 'ingredient',
      cost_per_unit: live?.cost_per_unit ?? (ing.quantity ? (ing.cost || 0) / ing.quantity : 0),
    };
  };

  const openItem = (item) => {
    const recipe = recipes.find((r) => r.menu_item_id === item.id);
    setSelectedItem({ ...item, recipe });
    setLines((recipe?.ingredients || []).map(editableLine));
    setEditing(false);
    setIngredientSearch('');
  };
  const closeItem = () => { setSelectedItem(null); setEditing(false); };
  // Clear: back to the recipe as saved (or none), without closing.
  const clearEdit = () => {
    setLines((selectedItem?.recipe?.ingredients || []).map(editableLine));
    setEditing(!selectedItem?.recipe);
    setIngredientSearch('');
  };

  const addLine = (ing) => {
    if (lines.some((l) => l.ingredient_id === ing.id)) { toast.error('Already in this recipe'); return; }
    setLines([...lines, { ingredient_id: ing.id, name: ing.name, quantity: 1, unit: ing.unit, source: ing.source || 'ingredient', cost_per_unit: ing.cost_per_unit }]);
    setIngredientSearch('');
  };
  const setQty = (id, quantity) => setLines(lines.map((l) => (l.ingredient_id === id ? { ...l, quantity: parseFloat(quantity) || 0 } : l)));
  const removeLine = (id) => setLines(lines.filter((l) => l.ingredient_id !== id));
  const cost = (ls) => ls.reduce((sum, l) => sum + l.quantity * l.cost_per_unit, 0);

  const saveRecipe = async () => {
    if (lines.length === 0) { toast.error('Add at least one ingredient'); return; }
    setSavingRecipe(true);
    try {
      const { data: saved } = await recipesApi.create({
        menu_item_id: selectedItem.id,
        ingredients: lines.map((l) => ({ ingredient_id: l.ingredient_id, source: l.source || 'ingredient', quantity: l.quantity, unit: l.unit })),
      });
      setRecipes((prev) => [...prev.filter((r) => r.menu_item_id !== selectedItem.id), saved]);
      setSelectedItem({ ...selectedItem, recipe: saved });
      setEditing(false);
      toast.success(`${selectedItem.name}'s recipe saved`);
    } catch {
      toast.error("Couldn't save the recipe");
    } finally {
      setSavingRecipe(false);
    }
  };

  // Record what's in an ingredient or sub-recipe, from the recipe that asked.
  const saveAllergens = async (line, keys) => {
    try {
      if (line.source === 'sub_recipe') {
        const sub = subRecipes.find((r) => r.id === line.ingredient_id);
        const { data } = await subRecipesApi.update(sub.id, { name: sub.name, yield_qty: sub.yield_qty, yield_unit: sub.yield_unit, batch_cost: sub.batch_cost, notes: sub.notes || '', allergens: keys });
        setSubRecipes((xs) => xs.map((x) => (x.id === sub.id ? { ...x, ...data, allergens: keys } : x)));
      } else {
        const ing = ingredients.find((i) => i.id === line.ingredient_id);
        const { data } = await ingredientsApi.update(ing.id, { name: ing.name, unit: ing.unit, cost_per_unit: ing.cost_per_unit, supplier_id: ing.supplier_id || null, stock_item_id: ing.stock_item_id || null, allergens: keys });
        setIngredients((xs) => xs.map((x) => (x.id === ing.id ? data : x)));
      }
      setAskingAllergens(null);
      toast.success(keys.length ? `${line.name}: ${words(keys)}` : `${line.name} has none of the 14`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record its allergens");
    }
  };

  const createNewIngredient = async () => {
    if (!newIngredient.name || !newIngredient.cost_per_unit) { toast.error('A name and a cost, please'); return; }
    try {
      const { data: created } = await ingredientsApi.create({
        name: newIngredient.name, cost_per_unit: parseFloat(newIngredient.cost_per_unit), unit: newIngredient.unit,
        supplier_id: newIngredient.supplier_id || null,
      });
      setIngredients([...ingredients, created]);
      addLine({ ...created, source: 'ingredient' });
      setNewIngredientOpen(false);
      setNewIngredient({ name: '', cost_per_unit: '', unit: 'kg', supplier_id: '' });
      toast.success(`${created.name} added to the recipe`);
    } catch {
      toast.error("Couldn't create the ingredient");
    }
  };

  const itemsWithRecipes = menuItems.map((item) => ({ ...item, recipe: recipes.find((r) => r.menu_item_id === item.id) }));
  const filteredItems = itemsWithRecipes.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()) || item.category.toLowerCase().includes(search.toLowerCase()));
  const matches = ingredientSearch ? costables.filter((c) => c.name.toLowerCase().includes(ingredientSearch.toLowerCase())) : [];
  const categories = [...new Set(filteredItems.map((i) => i.category))].sort((a, b) => (sectionOrder[a] ?? 999) - (sectionOrder[b] ?? 999) || a.localeCompare(b));

  // Recipes and sub-recipes on one page (menu consolidation, wave 2); the tab is in the URL.
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'sub' ? 'sub' : 'recipes';
  const setTab = (t) => setParams(t === 'sub' ? { tab: 'sub' } : {}, { replace: true });
  const header = (
    <PageHeader
      title="Recipes"
      purpose="What goes into each dish, and what it costs to make."
      actions={<Segmented value={tab} onChange={setTab} options={[{ value: 'recipes', label: 'Recipes' }, { value: 'sub', label: 'Sub-recipes' }]} />}
    />
  );

  if (tab === 'sub') {
    return <div className="space-y-6" data-testid="recipes-page">{header}<SubRecipesPanel /></div>;
  }
  if (loading) return <div className="space-y-6">{header}<Skeleton className="h-96" /></div>;

  const shownLines = editing ? lines : (selectedItem?.recipe?.ingredients || []).map(editableLine);
  const shownCost = editing ? cost(lines) : (selectedItem?.recipe?.total_cost || 0);
  const derived = fromRecipe(shownLines, allergensOf);
  const diff = selectedItem ? compareWithDish(derived.has, selectedItem.allergens) : { notOnDish: [], notInRecipe: [] };
  const hasRecipe = !!selectedItem?.recipe;

  return (
    <div className="space-y-6" data-testid="recipes-page">
      {header}
      <SearchField className="max-w-md" placeholder="Search dishes…" value={search} onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch('')} data-testid="recipe-search" />

      {categories.map((category) => (
        <section key={category} className="rounded-lg border border-hairline bg-white">
          <h2 className="border-b border-hairline px-5 py-3 font-heading text-[15px] font-extrabold text-ink">{category}</h2>
          <ul className="divide-y divide-hairline">
            {filteredItems.filter((i) => i.category === category).map((item) => {
              const margin = item.recipe && item.price ? ((item.price - (item.recipe.total_cost || 0)) / item.price) * 100 : null;
              return (
                <li key={item.id}>
                  <button type="button" onClick={() => openItem(item)} className="flex w-full items-center gap-4 px-5 py-3 text-left hover:bg-hover-tint" data-testid={`recipe-card-${item.id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-ink">{item.name}</span>
                      <span className="block truncate text-[12.5px] text-ink-faint">{item.description}</span>
                    </span>
                    {item.recipe ? (
                      <span className="shrink-0 text-[13px] text-ink-soft tabular-nums">
                        {item.recipe.ingredients?.length || 0} ingredients · costs {money(item.recipe.total_cost)}{margin != null && ` · ${margin.toFixed(0)}% margin`}
                      </span>
                    ) : <Signal tone="warn" dot={false}>no recipe</Signal>}
                    <span className="w-16 shrink-0 text-right font-mono text-[13.5px] tabular-nums text-ink">{money(item.price)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {filteredItems.length === 0 && <p className="py-10 text-center text-ink-soft">No dishes match. <button type="button" className="text-verlo underline" onClick={() => setSearch('')}>Clear the search</button></p>}

      {/* The dish, in a modal (not a side drawer) */}
      <Dialog open={!!selectedItem} onOpenChange={(o) => { if (!o) closeItem(); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="recipe-dialog">
          {selectedItem && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedItem.name}</DialogTitle>
                <DialogDescription>{selectedItem.category} · on the menu at {money(selectedItem.price)}</DialogDescription>
              </DialogHeader>

              {(hasRecipe || editing) && (
                <Stats>
                  <Stat label="Costs to make" value={money(shownCost)} />
                  <Stat label="Gross profit" value={money(selectedItem.price - shownCost)} />
                  <Stat label="Margin" value={selectedItem.price ? `${(((selectedItem.price - shownCost) / selectedItem.price) * 100).toFixed(0)}%` : '—'} />
                </Stats>
              )}

              {!hasRecipe && !editing ? (
                <div className="border border-hairline bg-surface-2 px-4 py-6 text-center">
                  <p className="text-ink-soft">No recipe yet, so this dish's cost and margin aren't known.</p>
                  <Button className="mt-3 bg-verlo hover:bg-verlo-hover" onClick={() => setEditing(true)} data-testid="edit-recipe-btn">Create recipe</Button>
                </div>
              ) : (
                <div className="space-y-2" data-testid="recipe-lines">
                  {editing && (
                    <div className="space-y-2">
                      <SearchField placeholder="Add an ingredient or sub-recipe…" value={ingredientSearch} onChange={(e) => setIngredientSearch(e.target.value)} onClear={() => setIngredientSearch('')} data-testid="ingredient-search" />
                      {ingredientSearch && (
                        <div className="max-h-44 overflow-y-auto border border-hairline bg-white">
                          {matches.slice(0, 8).map((ing) => (
                            <button key={ing.id} type="button" onClick={() => addLine(ing)} className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-hover-tint">
                              <span className="text-[13.5px] text-ink">{ing.name}{ing.source === 'sub_recipe' && <span className="ml-2 text-[12px] text-ink-faint">sub-recipe</span>}</span>
                              <span className="font-mono text-[12.5px] text-ink-faint">{money(ing.cost_per_unit)} / {ing.unit}</span>
                            </button>
                          ))}
                          {!matches.length && <p className="px-3 py-2 text-[13px] text-ink-soft">Nothing called that.</p>}
                          <button type="button" onClick={() => { setNewIngredient({ ...newIngredient, name: ingredientSearch }); setNewIngredientOpen(true); }}
                            className="w-full border-t border-hairline px-3 py-2 text-left text-[13px] font-semibold text-verlo hover:bg-hover-tint" data-testid="create-ingredient-btn">
                            + A new ingredient{ingredientSearch ? `: ${ingredientSearch}` : ''}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {shownLines.map((l) => {
                    const known = allergensOf(l);
                    return (
                      <div key={l.ingredient_id} className="border border-hairline bg-white px-3 py-2.5">
                        <div className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-[14px] font-semibold text-ink">{l.name}{l.source === 'sub_recipe' && <span className="ml-2 text-[12px] font-normal text-ink-faint">sub-recipe</span>}</p>
                            <p className="text-[12.5px] text-ink-faint">
                              {money(l.cost_per_unit)} / {l.unit}
                              {' · '}
                              {known == null
                                ? <button type="button" className="font-semibold text-signal-warn hover:underline" onClick={() => setAskingAllergens(askingAllergens === l.ingredient_id ? null : l.ingredient_id)} data-testid={`recipe-ask-allergens-${l.ingredient_id}`}>Allergens not recorded: say what's in it</button>
                                : (
                                  <>
                                    {known.length ? words(known) : 'none of the 14'}{' '}
                                    <button type="button" className="text-verlo hover:underline" onClick={() => setAskingAllergens(askingAllergens === l.ingredient_id ? null : l.ingredient_id)} data-testid={`recipe-change-allergens-${l.ingredient_id}`}>change</button>
                                  </>
                                )}
                            </p>
                          </div>
                          {editing ? (
                            <>
                              <Input type="number" step="0.1" min="0" value={l.quantity} onChange={(e) => setQty(l.ingredient_id, e.target.value)} className="h-9 w-20 text-center" aria-label={`${l.name} quantity`} />
                              <span className="w-10 text-[13px] text-ink-soft">{l.unit}</span>
                            </>
                          ) : <span className="text-[13px] text-ink-soft">{l.quantity} {l.unit}</span>}
                          <span className="w-16 text-right font-mono text-[13.5px] tabular-nums text-ink">{money(l.quantity * l.cost_per_unit)}</span>
                          {editing && (
                            <button type="button" onClick={() => removeLine(l.ingredient_id)} className="rounded p-1.5 text-ink-faint hover:bg-signal-stop-tint hover:text-signal-stop" aria-label={`Take ${l.name} out`}><Trash2 size={15} /></button>
                          )}
                        </div>
                        {askingAllergens === l.ingredient_id && <AllergenAsk line={l} initial={known || []} onSave={(keys) => saveAllergens(l, keys)} onCancel={() => setAskingAllergens(null)} />}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* What's in it, worked out from the ingredients (D12) */}
              {shownLines.length > 0 && (
                <div className="space-y-2" data-testid="recipe-allergens">
                  <p className="text-[13.5px] text-ink">
                    <span className="font-semibold">Allergens from the ingredients:</span>{' '}
                    {derived.unknown.length === shownLines.length
                      ? <span className="text-ink-soft">not known yet. None of its ingredients has its allergens recorded.</span>
                      : <>
                          {derived.has.length ? words(derived.has) : 'none of the 14'}
                          {derived.unknown.length > 0 && <span className="text-ink-faint"> so far; {derived.unknown.length} ingredient{derived.unknown.length === 1 ? '' : 's'} not recorded</span>}
                        </>}
                  </p>
                  {/* The dangerous way round always shows; "the dish lists more" waits until every
                      ingredient is recorded, or it would only be saying we don't know. */}
                  {(diff.notOnDish.length > 0 || (derived.unknown.length === 0 && diff.notInRecipe.length > 0)) && (
                    <Notice tone="stop" word="Allergens" title="Check the dish's allergens" data-testid="recipe-allergen-diff">
                      {diff.notOnDish.length > 0 && <>The ingredients have {words(diff.notOnDish)}, which the dish doesn't list. </>}
                      {derived.unknown.length === 0 && diff.notInRecipe.length > 0 && <>The dish lists {words(diff.notInRecipe)}, which no ingredient has. </>}
                      Change them on the dish, in Items.
                    </Notice>
                  )}
                </div>
              )}

              {(hasRecipe || editing) && (
                <DialogFooter className="sm:justify-between">
                  {editing ? <Button variant="outline" onClick={clearEdit}>Clear</Button> : <span />}
                  {editing
                    ? <Button className="bg-verlo hover:bg-verlo-hover" onClick={saveRecipe} disabled={savingRecipe} data-testid="save-recipe-btn">{savingRecipe ? 'Saving…' : 'Save recipe'}</Button>
                    : <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-recipe-btn">Change the recipe</Button>}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newIngredientOpen} onOpenChange={setNewIngredientOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>A new ingredient</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label htmlFor="ingredient-name">Name</Label>
              <Input id="ingredient-name" placeholder="e.g. Chicken thighs" value={newIngredient.name} onChange={(e) => setNewIngredient({ ...newIngredient, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label htmlFor="ingredient-cost">Cost per unit (£)</Label>
                <Input id="ingredient-cost" type="number" step="0.01" placeholder="0.00" value={newIngredient.cost_per_unit} onChange={(e) => setNewIngredient({ ...newIngredient, cost_per_unit: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Unit</Label>
                <Select value={newIngredient.unit} onValueChange={(v) => setNewIngredient({ ...newIngredient, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['kg', 'g', 'L', 'ml', 'each', 'portion', 'bottle', 'keg'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Supplier</Label>
              <Select value={newIngredient.supplier_id} onValueChange={(v) => setNewIngredient({ ...newIngredient, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewIngredientOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={createNewIngredient}>Add it to the recipe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Ask what's in an ingredient: the 14, as chips; none picked and saved means it has none.
function AllergenAsk({ line, initial = [], onSave, onCancel }) {
  const [picked, setPicked] = useState(initial);
  return (
    <div className="mt-2.5 border-t border-hairline pt-2.5" data-testid={`allergen-ask-${line.ingredient_id}`}>
      <p className="mb-2 text-[12.5px] text-ink-soft">Which of the 14 are in {line.name}? It's kept on the {line.source === 'sub_recipe' ? 'sub-recipe' : 'ingredient'}, for every recipe that uses it.</p>
      <div className="flex flex-wrap gap-1.5">
        {ALLERGENS.map((a) => {
          const on = picked.includes(a.key);
          return (
            <button key={a.key} type="button" aria-pressed={on}
              onClick={() => setPicked((p) => (p.includes(a.key) ? p.filter((k) => k !== a.key) : [...p, a.key]))}
              className={`rounded-full border px-2.5 py-1 text-[12.5px] ${on ? 'border-signal-stop bg-signal-stop-tint font-semibold text-signal-stop' : 'border-hairline-strong text-ink hover:bg-hover-tint'}`}>
              {a.label}
            </button>
          );
        })}
      </div>
      <div className="mt-2.5 flex gap-2">
        <Button size="sm" className="bg-verlo hover:bg-verlo-hover" onClick={() => onSave(picked)} data-testid="allergen-ask-save">{picked.length ? 'Save' : 'It has none of them'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Not now</Button>
      </div>
    </div>
  );
}

export default RecipesPage;
